import React, { useEffect, useState } from 'react';
import { ProductPhoto } from './NegadosCards';
import { IndicadoresService } from '../../services/indicadores.service';
import { colors } from '../../styles/tokens';
import type {
  DashboardFiltros,
  NegadoDetalleRow,
  NegadoProductoRow,
} from '../../types/indicadores.types';
import { fmtInt } from '../../utils/format';
import { useTableSort } from '../../utils/sort';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface DetalleNegadoModalProps {
  /** Fila de "Productos más negados" sobre la que se hizo clic (drill). */
  producto: NegadoProductoRow;
  /**
   * MISMOS rango/estatus/alcance de cadena-sucursal que ya están activos en
   * la vista Global — el drill NO trae su propio selector, hereda el de
   * `IndicadoresScreen` para que el desglose cuadre con lo que el usuario ya
   * está viendo en "Productos más negados".
   */
  filtros: DashboardFiltros;
  onClose: () => void;
}

/** `YYYY-MM-DD` -> `DD/MM` (mismo recorte por `-` que `formatDateRangeLabel` en `utils/aggregate.ts`). */
function formatDia(fecha: string): string {
  const [, mm, dd] = fecha.split('-');
  return `${dd}/${mm}`;
}

const thBase: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 700,
  cursor: 'pointer',
  userSelect: 'none',
};

/** Columna ordenable de la tabla del drill (mismo patrón `renderSortableTh` de `NegadosCards`). */
interface Columna {
  key: keyof NegadoDetalleRow;
  label: string;
  style: React.CSSProperties;
  testId: string;
}

const COLUMNAS: Columna[] = [
  { key: 'fecha', label: 'Día', style: { ...thBase, textAlign: 'left', padding: '10px 16px' }, testId: 'th-detalle-fecha' },
  { key: 'hora', label: 'Hora', style: { ...thBase, textAlign: 'left', padding: '10px 10px' }, testId: 'th-detalle-hora' },
  { key: 'sucursal', label: 'Sucursal', style: { ...thBase, textAlign: 'left', padding: '10px 10px' }, testId: 'th-detalle-sucursal' },
  { key: 'talla', label: 'Talla', style: { ...thBase, textAlign: 'right', padding: '10px 12px' }, testId: 'th-detalle-talla' },
  { key: 'negados', label: 'Negados', style: { ...thBase, textAlign: 'right', padding: '10px 16px' }, testId: 'th-detalle-negados' },
];

const boxMessageStyle: React.CSSProperties = {
  padding: '28px 20px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: 13,
};

/**
 * Modal del drill "Detalle Negado" (PT7, equivalente a la tabla homónima del
 * Power BI): se abre al hacer clic en una fila de "Productos más negados" y
 * muestra el desglose día/hora/sucursal/talla de negado de ESE producto,
 * respetando el rango/estatus/alcance ya vigentes en la vista Global
 * (`filtros`, heredados tal cual — ver `IndicadoresScreen`).
 *
 * Cierra con clic en el backdrop o tecla Esc (patrón ya usado por `Combo`
 * para su panel, aquí con un listener de `keydown` en `document` porque el
 * modal no tiene un contenedor propio con foco). Reusa `ProductPhoto` de
 * `NegadosCards` (NO se duplica) y `useTableSort`/`fmtInt` del resto del
 * módulo.
 */
const DetalleNegadoModal: React.FC<DetalleNegadoModalProps> = ({ producto, filtros, onClose }) => {
  const esMovil = useEsMovil();
  const [filas, setFilas] = useState<NegadoDetalleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    IndicadoresService.getNegadosDetalle(producto.productId, filtros)
      .then((rows) => {
        if (!cancelled) {
          setFilas(rows);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar el detalle.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Deps en campos PRIMITIVOS de `filtros` (no el objeto completo): igual
    // que el resto de los efectos de `IndicadoresScreen`, `filtros` llega como
    // un literal `{...}` que se re-crea en cada render del padre — depender
    // de la referencia completa dispararía un refetch en CADA render de
    // `IndicadoresScreen` (soloActivas, hover, etc.), no solo cuando el rango/
    // estatus/alcance realmente cambian.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    producto.productId,
    filtros.desde,
    filtros.hasta,
    filtros.chain,
    filtros.branchOffice,
    filtros.status,
  ]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const { sorted, sort, toggle } = useTableSort(filas, 'fecha', 'desc');

  return (
    <div
      data-testid="detalle-negado-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,23,29,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        // En telefono 20 px por lado se comen 40 de los 390: el modal se pega
        // mas al borde para dejarle el ancho a la tabla de dentro. Mismo
        // criterio que los otros tres modales del modulo.
        padding: esMovil ? 10 : 20,
      }}
    >
      <div
        data-testid="detalle-negado-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.card,
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            background: colors.brand,
            color: '#fff',
            padding: esMovil ? '12px 14px' : '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: esMovil ? 10 : 14,
          }}
        >
          <ProductPhoto imageUrl={producto.imageUrl} alt={producto.descripcion} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: '.02em' }}>
              {producto.descripcion}
            </div>
            <div style={{ fontSize: 11, opacity: 0.82, fontWeight: 600, marginTop: 1 }}>
              ICOD {producto.icod}
            </div>
          </div>
          <button
            type="button"
            data-testid="detalle-negado-cerrar"
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: esMovil ? 22 : 20,
              lineHeight: 1,
              cursor: 'pointer',
              padding: 4,
              // Regla 4: 44 px de area tocable en telefono.
              ...(esMovil ? { minHeight: 44, minWidth: 44, flex: 'none' } : null),
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <div data-testid="detalle-negado-loading" style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
            Cargando detalle…
          </div>
        )}

        {!loading && error && (
          <div data-testid="detalle-negado-error" style={{ ...boxMessageStyle, color: colors.rojo }}>
            No se pudo cargar el detalle: {error}
          </div>
        )}

        {!loading && !error && (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: '#fff',
                    boxShadow: 'inset 0 -1px 0 #e7e9ef',
                  }}
                >
                  {COLUMNAS.map((col) => (
                    <th
                      key={String(col.key)}
                      data-testid={col.testId}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggle(col.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(col.key);
                        }
                      }}
                      style={col.style}
                    >
                      {col.label}
                      {sort.key === col.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((f, i) => (
                  <tr
                    key={`${f.fecha}|${f.hora}|${f.sucursal ?? ''}|${f.talla}|${i}`}
                    style={{ borderBottom: `1px solid ${colors.rowBorder}` }}
                  >
                    <td style={{ padding: '10px 16px', fontWeight: 700, color: colors.texto.strong }}>
                      {formatDia(f.fecha)}
                    </td>
                    <td style={{ padding: '10px 10px', color: colors.texto.body }}>{f.hora}:00</td>
                    <td style={{ padding: '10px 10px', color: colors.texto.body }}>{f.sucursal ?? '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.texto.secondary, fontWeight: 600 }}>
                      {f.talla}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: colors.rojo }}>
                      {fmtInt(f.negados)}
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ ...boxMessageStyle, color: colors.texto.tertiary }}>
                      Sin ocurrencias de negado en el periodo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalleNegadoModal;
